import bgImg from 'assets/QRAChartsPageBackground.png'

import { IStateAssessment } from 'features/RiskManagement/Assessments'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'

interface IWISAssessmentIncompletePlaceholderProps {
  assessment: IStateAssessment | undefined
  isLoading: boolean
}

type StatusInfo = {
  title: string
  description: string
}

/**
 * helper method to display title/description.
 */
const getAssessmentStatusInfo = (
  assessment: IStateAssessment | undefined,
  isLoading: boolean
): StatusInfo => {
  // if Loading, default to loading text
  if (isLoading) {
    return {
      title: 'Awaiting Assessment Info',
      description: 'Loading..'
    }
  }

  // Only if NOT LOADING, show the deleted message
  if (!assessment) {
    return {
      title: 'Assessment Deleted',
      description: 'Assessment has been deleted.'
    }
  }

  switch (assessment.statusCode) {
    case WISAssessmentStatusCode.New:
    case WISAssessmentStatusCode.DataPreparation:
      return {
        title: 'Assessment Queued',
        description: 'Assessment should begin shortly...'
      }

    case WISAssessmentStatusCode.InProcess:
      return {
        title: 'Assessment Processing',
        description: [
          `Total Segments: ${assessment.totalSegmentCount || ''}`,
          `Segments Processed: ${assessment.counterState || ''}`,
          `Estimate Time Remaining: ${
            assessment.remainingMinutesEstimation
              ? assessment.remainingMinutesEstimation + ' minutes'
              : ''
          }`
        ].join('\n')
      }

    case WISAssessmentStatusCode.RunningModels:
      return {
        title: 'Assessment Processing',
        description: 'Running Risk Models'
      }

    case WISAssessmentStatusCode.PostProcessing:
      return {
        title: 'Assessment Processing',
        description: 'Post processing results'
      }

    case WISAssessmentStatusCode.PopulatingToDB:
      return {
        title: 'Assessment Populating',
        description: 'Propagating changes to database'
      }

    case WISAssessmentStatusCode.PopulatingToMap:
      return {
        title: 'Assessment Populating',
        description: 'Propagating changes to map service'
      }

    // REVIEW: Logic may not be necessary after wire up of system/line view to saved states RTK query
    case WISAssessmentStatusCode.Completed:
      return {
        title: 'Assessment Completed',
        description: 'Click on the notification to view assessment.'
      }

    // REVIEW: [LHT-5266]: (RiskMgmt FE, WIS error handling):
    // Use backend-provided statusDescription for this error message,
    // falling back to the generic text when no detailed error is available.
    case WISAssessmentStatusCode.Error:
      return {
        title: 'Assessment Error!',
        // description: `Error: ${assessment.statusDescription ?? ''}`
        description: 'An error occurred while processing the assessment.'
      }

    default:
      return {
        title: 'Unknown Status',
        description: ''
      }
  }
}

const WISAssessmentIncompletePlaceholder = ({
  assessment,
  isLoading
}: IWISAssessmentIncompletePlaceholderProps) => {
  const { title, description } = getAssessmentStatusInfo(assessment, isLoading)

  return (
    <div
      className='flex flex-1 flex-col bg-gray-100 bg-cover'
      style={{ backgroundImage: `url(${bgImg})` }}
    >
      <div className='flex flex-3 items-center justify-center'>
        <div className='w-96 rounded bg-white p-8 shadow-lg'>
          <div className='text-2xl font-semibold'>{title}</div>
          <p className='my-4 whitespace-pre-line text-lg font-medium text-gray-500'>
            {description}
          </p>
        </div>
      </div>
      <div className='flex-1' />
    </div>
  )
}

export default WISAssessmentIncompletePlaceholder
